import JSZip from "jszip";
import { HACIENDA_RESPONSE_ROOT, readRootName } from "./parseEDocumentXml";

/** Tope de comprobantes por carga. Mas que esto pide procesar el zip en backend. */
export const MAX_EDOCUMENTS_PER_UPLOAD = 500;

export type ReadEDocumentFile = {
    fileName: string;
    xml: string;
    rootName: string;
};

export type DiscardedEDocumentFile = {
    fileName: string;
    reason: string;
};

export type ReadEDocumentFilesResult = {
    files: ReadEDocumentFile[];
    discarded: DiscardedEDocumentFile[];
    /** Comprobantes que quedaron fuera por el tope de la carga. */
    truncatedCount: number;
};

export class EDocumentFileError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "EDocumentFileError";
    }
}

const isXmlName = (name: string): boolean => name.toLowerCase().endsWith(".xml");

const isZipName = (name: string): boolean => name.toLowerCase().endsWith(".zip");

/**
 * Archivos que aparecen en los zips exportados pero no son comprobantes.
 *
 * macOS mete `__MACOSX/` con copias de recursos, y los sistemas de facturacion guardan la
 * respuesta de Hacienda junto al comprobante con el sufijo `-respuesta`.
 */
const isNoiseEntry = (name: string): boolean => {
    const lower = name.toLowerCase();
    return (
        lower.startsWith("__macosx/") ||
        lower.includes("/__macosx/") ||
        lower.split("/").pop()?.startsWith("._") === true
    );
};

const isHaciendaResponseName = (name: string): boolean => {
    const base = name.toLowerCase().split("/").pop() || "";
    return base.includes("-respuesta") || base.includes("_respuesta");
};

const baseName = (path: string): string => path.split("/").pop() || path;

/** Clasifica un XML ya leido: comprobante utilizable o descarte con motivo. */
const classify = (
    fileName: string,
    xml: string,
    acceptedRootNames: string[]
): ReadEDocumentFile | DiscardedEDocumentFile => {
    let rootName = "";
    try {
        rootName = readRootName(xml);
    } catch {
        return { fileName, reason: "El archivo no es un XML valido" };
    }

    if (!rootName) {
        return { fileName, reason: "El archivo no es un XML valido" };
    }
    if (rootName === HACIENDA_RESPONSE_ROOT) {
        return { fileName, reason: "Es una respuesta de Hacienda, no un comprobante" };
    }
    if (!acceptedRootNames.includes(rootName)) {
        return { fileName, reason: `Este tipo de comprobante (${rootName}) no se carga aqui` };
    }

    return { fileName, xml, rootName };
};

const isDiscarded = (
    entry: ReadEDocumentFile | DiscardedEDocumentFile
): entry is DiscardedEDocumentFile => "reason" in entry;

/**
 * Lee los comprobantes de un `.xml` suelto o de un `.zip`.
 *
 * Recorre el zip completo (incluidas las carpetas anidadas, que es como los sistemas exportan
 * por mes o por sucursal) y separa lo utilizable del ruido, devolviendo el motivo de cada
 * descarte para poder mostrarlo en la revision. Es agnostico del tipo de comprobante: las
 * raices aceptadas las decide la definicion.
 */
export const readEDocumentFiles = async (
    file: File,
    acceptedRootNames: string[]
): Promise<ReadEDocumentFilesResult> => {
    const files: ReadEDocumentFile[] = [];
    const discarded: DiscardedEDocumentFile[] = [];

    if (isXmlName(file.name)) {
        const entry = classify(file.name, await file.text(), acceptedRootNames);
        if (isDiscarded(entry)) discarded.push(entry);
        else files.push(entry);

        return { files, discarded, truncatedCount: 0 };
    }

    if (!isZipName(file.name)) {
        throw new EDocumentFileError("Solo se aceptan archivos XML o un ZIP con archivos XML");
    }

    let zip: JSZip;
    try {
        zip = await JSZip.loadAsync(await file.arrayBuffer());
    } catch {
        throw new EDocumentFileError("No fue posible leer el archivo ZIP");
    }

    const entries = Object.values(zip.files).filter((entry) => !entry.dir && !isNoiseEntry(entry.name));

    let truncatedCount = 0;

    for (const entry of entries) {
        const fileName = baseName(entry.name);

        if (!isXmlName(entry.name)) {
            discarded.push({ fileName, reason: "No es un archivo XML" });
            continue;
        }
        if (isHaciendaResponseName(entry.name)) {
            discarded.push({ fileName, reason: "Es una respuesta de Hacienda, no un comprobante" });
            continue;
        }
        // El tope se cuenta solo contra los comprobantes utilizables, para que un zip lleno
        // de respuestas de Hacienda no consuma el cupo.
        if (files.length >= MAX_EDOCUMENTS_PER_UPLOAD) {
            truncatedCount += 1;
            continue;
        }

        const classified = classify(fileName, await entry.async("string"), acceptedRootNames);
        if (isDiscarded(classified)) discarded.push(classified);
        else files.push(classified);
    }

    if (!files.length && !discarded.length) {
        throw new EDocumentFileError("El archivo ZIP no contiene comprobantes XML");
    }

    return { files, discarded, truncatedCount };
};
